import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input, OnChanges, SimpleChange, SimpleChanges } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { AddressSelectionDialogComponent } from '../dialogs/address-selection-dialog/address-selection-dialog.component';
import { CopyLinesDialogComponent } from '../dialogs/copy-lines-dialog/copy-lines-dialog.component';
import { EntityBreakDialogComponent } from '../dialogs/entity-break-dialog/entity-break-dialog.component';
import { EntityCancelDialogComponent } from '../dialogs/entity-cancel-dialog/entity-cancel-dialog.component';
import { LeadTimeDialogComponent } from '../dialogs/lead-time-dialog/lead-time-dialog.component';
import { LineSummaryDialogComponent } from '../dialogs/line-summary-dialog/line-summary-dialog.component';
import { RiserDialogComponent } from '../dialogs/riser-dialog/riser-dialog.component';
import { SalesRepDialogComponent } from '../dialogs/sales-rep-dialog/sales-rep-dialog.component';
import { TaggingDialogComponent } from '../dialogs/tagging-dialog/tagging-dialog.component';
import { WarrantyTextDialogComponent } from '../dialogs/warranty-text-dialog/warranty-text-dialog.component';
import { SkyportContactDialogComponent } from '../dialogs/skyport-contact-dialog/skyport-contact-dialog.component';
import { Entity, EntityKey } from '../entity/entity';
import { EntityLineHeader } from '../entity/entity-line-header';
import { PersonLogin, RoleName } from '../person/person-login';
import { SelectionItem } from '../selections/selection-item';
import { EntityLineService } from '../services/entity-line-service';
import { EntityService } from '../services/entity-service';
import { IecTaskService } from '../services/iec-task-service';
import { OrganizationService } from '../services/organization-service';
import { PersonLoginService } from '../services/person-login-service';
import { PersonService } from '../services/person-service';
import { QuoteService } from '../services/quote-service';
import { SelectionService } from '../services/selection-service';
import { SfrManagementService } from '../services/sfr-management-service';
import { DateUtils } from '../utils/date-utils';
import { StringUtils } from '../utils/string-utils';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EntityComplexity } from '../model/entity-complexity';
import { EntityComplexityService } from '../services/entity-complexity.service';
import { ComplexityInfoDialogComponent } from '../dialogs/complexity-info-dialog/complexity-info-dialog.component';
import { ExtendedWarrantyUtils } from '../utils/extended-warranty-utils';
import { ExtendedWarrantyDialogComponent } from '../dialogs/extended-warranty-dialog/extended-warranty-dialog.component';
import { EntityLineUtils } from '../utils/entity-line-utils';
import { LeadTimeUtils } from '../utils/lead-time-utils';
import { GenericMessageComponent } from '../dialogs/generic-message/generic-message.component';
import { QewlDialogComponent } from '../dialogs/qewl-dialog/qewl-dialog.component';
import { SelectParentEntityDialogComponent } from '../dialogs/select-parent-entity-dialog/select-parent-entity-dialog.component';
import { DialogService } from '../services/dialog.service';
import { OpportunityService } from '../services/opportunity-service';
import { Opportunity } from '../opportunity/opportunity';
import { RequireOpportunityUpdatesDialogComponent } from '../dialogs/require-opp-updates-dialog/require-opp-updates-dialog.component';
import { EnterContactDialogComponent } from '../dialogs/enter-contact-dialog/enter-contact-dialog.component';
import { MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { NgClass, DecimalPipe, CurrencyPipe, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FpaService } from '../services/fpa-service';
import { EntityKeyUtils } from '../utils/entity-key-utils';
import { UnrestrictedEntityUpdate } from '../model/unrestricted-entity-update';
import { EntitySaveResponse } from '../entity/entity-save-response';

@Component({
  selector: 'quote-detail',
  templateUrl: './quote-detail.component.html',
  styleUrls: ['./quote-detail.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    MatButton,
    MatIcon,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    RouterLink,
    MatProgressSpinner,
    NgClass,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatDatepicker,
    DecimalPipe,
    CurrencyPipe,
    DatePipe,
  ],
})
export class QuoteDetailComponent implements OnInit, OnChanges {
  readonly quoteId = input.required<string>({ alias: 'quote-id' });
  readonly quoteRevision = input.required<string>({ alias: 'quote-revision' });
  readonly orgUnitId = input<string>(undefined!);
  private readonly personLoginService = inject(PersonLoginService);
  private readonly selectionService = inject(SelectionService);
  private readonly organizationService = inject(OrganizationService);
  private readonly quoteService = inject(QuoteService);
  private readonly entityService = inject(EntityService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly entityLineService = inject(EntityLineService);
  private readonly sfrService = inject(SfrManagementService);
  private readonly personService = inject(PersonService);
  private readonly entityComplexityService = inject(EntityComplexityService);
  private readonly dialogService = inject(DialogService);
  private readonly opportunityService = inject(OpportunityService);
  private readonly fpaService = inject(FpaService);
  private readonly iecTaskService = inject(IecTaskService);
  private personLogin!: PersonLogin;
  private readonly canCancelFpaReview = signal<boolean>(false);
  private readonly canSubmitFpa = signal<boolean>(false);
  readonly isRepUser = signal<boolean>(undefined!);
  public controllingOrgs!: SelectionItem[];
  readonly sellingOrgDescription = signal<string>(undefined!);
  readonly shippingHandlingDescription = signal<string>(undefined!);
  readonly quote = signal<Entity>(undefined!);
  readonly associatedOrders = signal<EntityKey[]>(undefined!);
  readonly hasOpenBypassTask = signal<boolean>(false);
  readonly isBusy = signal<boolean>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  readonly isNew = signal<boolean>(undefined!);
  readonly customers = signal<SelectionItem[]>(undefined!);
  readonly repOffices = signal<SelectionItem[]>(undefined!);
  readonly releaseTypes = signal<SelectionItem[]>(undefined!);
  readonly fobs = signal<SelectionItem[]>(undefined!);
  readonly paymentTerms = signal<SelectionItem[]>(undefined!);
  readonly salesReps = signal<SelectionItem[]>(undefined!);
  readonly outsideSalesReps = signal<SelectionItem[]>(undefined!);
  readonly iecContacts = signal<SelectionItem[]>(undefined!);
  readonly leadTimes = signal<SelectionItem[]>(undefined!);
  readonly salesChannels = signal<SelectionItem[]>(undefined!);
  readonly useTypes = signal<SelectionItem[]>(undefined!);
  readonly sAndHChargeTypes = signal<SelectionItem[]>(undefined!);
  readonly shipVias = signal<SelectionItem[]>(undefined!);
  readonly deliveryCountries = signal<SelectionItem[]>(undefined!);
  readonly marketSegments = signal<SelectionItem[]>(undefined!);
  readonly projectTypes = signal<SelectionItem[]>(undefined!);
  readonly specTypes = signal<SelectionItem[]>(undefined!);
  readonly saveMessages = signal<string[]>(undefined!);
  readonly selectedLines = signal<EntityLineHeader[]>([]);
  public selectionStartIndex: number = -1;
  readonly marketSegmentMissing = signal<boolean>(false);
  readonly projectTypeMissing = signal<boolean>(false);
  readonly specTypeMissing = signal<boolean>(false);
  readonly skyportContactMissing = signal<boolean>(false);
  readonly taggingRoomMissing = signal<boolean>(false);
  readonly createdBy = signal<string>(undefined!);
  readonly updatedBy = signal<string>(undefined!);
  private readonly currentLeadTime = signal<string>('');
  readonly isSiblingOrgUser = signal<boolean>(false);
  readonly waitMessage = signal<string>('Please Wait...');
  readonly creditDocumentsNeeded = signal<boolean>(undefined!);
  readonly entityComplexity = signal<EntityComplexity>(undefined!);
  readonly showAssignNewOpportunityOption = signal<boolean>(false);
  readonly associatedOpportunity = signal<Opportunity>(undefined!);
  readonly hasMissingRiserData = signal<boolean>(false);
  readonly sentToEwl = signal<boolean>(false);

  ngOnInit(): void {
    if (this.personLoginService.isOutsideEngineer()) {
      this.router.navigate(['ratings']);
    } else {
      this.isBusy.set(true);
      this.personLogin = this.personLoginService.getCachedLogin()!;
      const primaryOrgUnit = this.personLogin.person.primaryOrgUnitId;
      if (primaryOrgUnit && primaryOrgUnit.length > 0) {
        this.organizationService.getOrganization(primaryOrgUnit).subscribe((organization) => {
          this.isSiblingOrgUser.set(this.organizationService.isSiblingOrg(organization));
          this.isRepUser.set(organization.typeCode == 'IREP' || organization.typeCode == 'OREP');
          this.initialize();
        });
      } else {
        this.isRepUser.set(false);
        this.initialize();
      }
    }
  }

  private handleError(error: any): void {
    this.isBusy.set(false);
    this.waitMessage.set('Please Wait...');
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (Object.values(changes).some((change: SimpleChange) => !change.firstChange)) {
      this.initialize();
    }
  }

  private initialize(): void {
    this.isNew.set(false);
    this.loadSelectionLists();
    const idString: string = this.quoteId();
    const revisionString: string = this.quoteRevision();
    this.hasMissingRiserData.set(false);
    if (idString && idString.length > 0 && revisionString) {
      if ('new' === idString) {
        this.startNewQuote(this.orgUnitId());
      } else {
        const key: EntityKey = new EntityKey();
        key.entityId = idString;
        key.entityName = 'QUOTE';
        key.entityRevision = 0;
        this.quoteService.isRepriceNeeded(key).subscribe({
          next: (result) => {
            if (result && result == 'true') {
              this.waitMessage.set('Updating List Prices...');
              this.quoteService.reprice(key).subscribe({
                next: (result) => {
                  this.loadQuoteDetails(result);
                },
                error: (error) => this.handleError(error),
              });
            } else {
              this.entityService.getEntity('QUOTE', idString, +revisionString).subscribe({
                next: (result) => {
                  this.loadQuoteDetails(result);
                },
                error: (error) => this.handleError(error),
              });
            }
          },
          error: (error) => this.handleError(error),
        });
      }
    } else {
      this.backToQuoteMaster();
    }
  }

  private loadQuoteDetails(quote: Entity) {
    this.waitMessage.set('Please Wait...');
    this.quote.set(quote);
    this.checkForMissingRiserData();
    this.checkForSentToEwl();
    this.checkForOpenBypassTask();
    this.updateFpaStatusFlags();
    this.quoteService.getAssociatedOrders(quote.id.entityId, quote.id.entityRevision).subscribe((associated) => (this.associatedOrders.set(associated)));
    this.personService.getByUserLogin(this.quote().createUser).subscribe((result) => {
      if (result) {
        this.createdBy.set(result.description);
      } else {
        this.createdBy.set(this.quote().createUser);
      }
    });
    this.entityComplexityService.getEntityComplexity(quote.id).subscribe((complexity) => (this.entityComplexity.set(complexity)));
    this.loadUpdateUser();
    this.loadSellingOrganizationSelections();
    this.updateOpportunityIfRequired();
    this.checkForCreditDocumentsNeeded();
    this.checkForDiscontinuedLines();
    this.isAssignNewOpportunityOptionDisplayed();
  }

  private checkForMissingRiserData(): void {
    const quoteId: EntityKey = this.quote()?.id;
    if (quoteId && StringUtils.isNotBlank(quoteId.entityId)) {
      this.entityService.hasMissingRiserData(quoteId).subscribe({
        next: (result: string) => {
          this.hasMissingRiserData.set(StringUtils.equalsIgnoreCase('true', result));
        },
        error: (error: any) => this.handleError(error),
      });
    }
  }



  private checkForOpenBypassTask(): void {
    this.hasOpenBypassTask.set(false);
    if (!this.quote() || !this.quote().id || !this.quote().id.entityId) {
      return;
    }
    const taskNames = ['Serial Number Bypass Review', 'SN Bypass Rejection Follow-up'];
    taskNames.forEach((name) => {
      this.iecTaskService.getActiveTasksByNameAndEntity(name, this.quote().id.entityId, this.quote().id.entityRevision).subscribe({
        next: (tasks) => {
          if (tasks && tasks.length > 0) {
            this.hasOpenBypassTask.set(true);
          }
        },
      });
    });
  }

  private checkForDiscontinuedLines(): void {
    const cutoffDate = new Date(2021, 7, 1);
    const createdDate = new Date(this.quote().createTimestamp);
    if (this.quote() && createdDate.toISOString() < cutoffDate.toISOString() && this.quote().lines) {
      const discontinuedLines = this.quote().lines.filter((line) => EntityLineUtils.hasDiscontinuedProduct(line)).map((line) => line.lineId);
      if (discontinuedLines.length > 0) {
        const message: string = 'Line(s) ' + discontinuedLines.join(', ') + ' have discontinued products and will need to be replaced with Vertical Floor A units';
        this.dialog.open(GenericMessageComponent, {
          width: '400px',
          maxWidth: '90vw',
          height: '200px',
          data: { message: message },
        });
      }
    }
  }

  private checkForCreditDocumentsNeeded(): void {
    this.creditDocumentsNeeded.set(false);
    if (this.personLoginService.isInternalOrRsm()) {
      this.quoteService.getCreditDocumentsRequired(this.quote().id).subscribe((results) => {
        this.creditDocumentsNeeded.set(results && results.length > 0);
      });
    }
  }

  private loadUpdateUser(): void {
    this.personService.getByUserLogin(this.quote().updateUser).subscribe({
      next: (result) => {
        if (result) {
          this.updatedBy.set(result.description);
        } else {
          this.updatedBy.set(this.quote().updateUser);
        }
      },
      error: () => (this.updatedBy.set(this.quote().updateUser)),
    });
  }

  public save(): void {
    this.resetStatus();
    this.quoteService.save(this.quote()).subscribe({
      next: (result) => {
        if (this.isNew()) {
          this.router.navigate(['quotes/' + result.updated.id.entityId + '/' + result.updated.id.entityRevision]);
        } else {
          this.quote.set(result.updated);
          this.checkForMissingRiserData();
        }
        this.isBusy.set(false);
        this.isNew.set(false);
        this.saveMessages.set(result.messages);
        this.loadUpdateUser();
        this.checkForCreditDocumentsNeeded();
      },
      error: (error) => {
        this.handleError(error);
      },
    });
  }

  private resetStatus(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.saveMessages.set(undefined!);
    this.marketSegmentMissing.set(false);
    this.projectTypeMissing.set(false);
    this.specTypeMissing.set(false);
    this.skyportContactMissing.set(false);
    this.taggingRoomMissing.set(false);
  }

  backToQuoteMaster(): void {
    this.router.navigate(['quotes']);
  }

  private startNewQuote(orgUnitId: string): void {
    this.isNew.set(true);
    this.quote.set(new Entity());
    this.quote().id = new EntityKey();
    this.quote().id.entityName = 'QUOTE';
    this.quote().updateAllowed = true;
    const today = DateUtils.formatOutput(new Date());
    const nullDate = DateUtils.formatOutput(new Date(1753, 0, 1));
    this.quote().createTimestamp = today;
    this.quote().createUser = this.personLogin.person.userLogin;
    this.createdBy.set(this.personLogin.person.description);
    const expirationDate = DateUtils.formatOutput(new Date());
    expirationDate.setFullYear(expirationDate.getFullYear() + 1);
    this.quote().expirationDate = expirationDate;
    this.quote().releaseTypeId = 'SR';
    this.quote().priceBasisDate = today;
    this.quote().sfrPriceBasisDate = today;
    this.quote().requestedShipDate = today;
    this.quote().estimatedScheduleDate = nullDate;
    this.quote().lastProcessDate = nullDate;
    this.quote().useTypeCode = 'IN';
    this.quote().advanceNoticeHours = 0;
    this.quote().deliveryCountryCode = 'USA';
    this.quote().totalList = 0;
    this.quote().netAmount = 0;
    this.quote().specialCharges = 0;
    this.quote().totalQuoteAmount = 0;
    this.quote().multiplier = 0;
    this.quote().unitQuantity = 0;
    this.quote().statusCode = 'NEW';
    this.quote().statusDescription = 'NEW';
    this.quote().approvalStatus = 'Pending Release';
    this.quote().frozen = false;
    if (this.isRepUser() && !this.isSiblingOrgUser()) {
      this.quote().repOfficeId = this.personLogin.person.primaryOrgUnitId;
      this.quote().customerId = this.personLogin.person.primaryOrgUnitId;
      this.loadSellingOrganizationSelections();
    } else {
      if (orgUnitId && orgUnitId.length > 0) {
        this.quote().repOfficeId = orgUnitId;
        this.quote().customerId = orgUnitId;
        this.loadSellingOrganizationSelections();
      } else {
        this.isBusy.set(false);
      }
    }
  }

  private loadSelectionLists(): void {
    this.selectionService.getControllingOrgs().subscribe((results) => (this.repOffices.set(results)));
    this.selectionService.getOrderTypes().subscribe((results) => {
      this.releaseTypes.set(results.filter((result) => 'SR' === result.code || 'MR' === result.code));
    });
    this.selectionService.getDeliveryTerms().subscribe((results) => (this.fobs.set(results)));
    this.selectionService.getPaymentTerms().subscribe((results) => (this.paymentTerms.set(results)));
    this.selectionService.getCustomerServiceReps().subscribe((results) => (this.iecContacts.set(results)));
    this.selectionService.getFreightTerms().subscribe((results) => (this.shipVias.set(results)));
    this.selectionService.getCountryCodes().subscribe((results) => (this.deliveryCountries.set(results)));
    this.selectionService.getSalesAreas().subscribe((results) => (this.marketSegments.set(results)));
    this.selectionService.getRegions().subscribe((results) => (this.projectTypes.set(results)));
    this.selectionService.getNewExtendCodes().subscribe((results) => (this.specTypes.set(results)));
  }

  public loadSellingOrganizationSelections(): void {
    this.customers.set(undefined!);
    this.salesReps.set(undefined!);
    this.outsideSalesReps.set(undefined!);
    this.leadTimes.set(undefined!);
    this.salesChannels.set(undefined!);
    this.useTypes.set(undefined!);
    this.sAndHChargeTypes.set(undefined!);
    if (this.quote() && this.quote().repOfficeId) {
      this.isBusy.set(true);
      this.organizationService.getOrganization(this.quote().repOfficeId).subscribe((sellingOrg) => {
        this.sellingOrgDescription.set(sellingOrg.description);
        this.shippingHandlingDescription.set(sellingOrg.transportationTermsDescription);
        this.selectionService.getCustomers(this.quote().repOfficeId).subscribe((results) => {
          if (results.filter((result) => result.code == this.quote().repOfficeId).length === 0) {
            const rep: SelectionItem = new SelectionItem();
            rep.code = sellingOrg.orgUnitId;
            rep.description = sellingOrg.description;
            results.unshift(rep);
          }
          this.customers.set(results);
          this.quote().customerId = this.selectFirstItemIfMissing(this.quote().customerId, results);
        });
        this.selectionService.getOrganizationPeople(this.quote().repOfficeId).subscribe({
          next: (results) => {
            this.salesReps.set(results);
            this.outsideSalesReps.set(results);
          },
        });
        this.selectionService.getOrganizationLeadTimes(this.quote().repOfficeId).subscribe((results) => {
          const visibleLeadTimes = this.isPaperPilotUser() ? results : results.filter((result) => !this.isPaperPilotLeadTime(result.code));
          this.leadTimes.set(visibleLeadTimes);
          this.quote().leadTimeCode = this.selectFirstItemIfMissing(this.quote().leadTimeCode, visibleLeadTimes);
        });
        this.selectionService.getOrganizationChannels(this.quote().repOfficeId).subscribe((results) => {
          this.salesChannels.set(results);
          this.quote().salesChannelCode = this.selectFirstItemIfMissing(this.quote().salesChannelCode, results);
        });
        this.selectionService.getOrganizationExpediteCodes(this.quote().repOfficeId).subscribe((results) => {
          this.useTypes.set(results);
          this.quote().useTypeCode = this.selectFirstItemIfMissing(this.quote().useTypeCode, results);
        });
        this.selectionService.getOrganizationTransportationTerms(this.quote().repOfficeId).subscribe((results) => (this.sAndHChargeTypes.set(results)));
        if (this.isNew()) {
          this.quote().iecContactId = sellingOrg.serviceRepId;
          this.quote().shipAndHandChargeType = sellingOrg.transportationTermsCode;
          this.quote().fobCode = sellingOrg.deliveryTermsCode;
          this.quote().paymentTermsCode = sellingOrg.paymentTermsCode;
          this.quote().shipViaCode = sellingOrg.freightTermsCode;
          this.quote().leadTimeCode = 'REG';
          if (sellingOrg.smallTextUserField5 && sellingOrg.smallTextUserField5.length > 0) {
            this.quote().useTypeCode = sellingOrg.smallTextUserField5;
          } else {
            this.quote().useTypeCode = 'IN';
          }
          if (this.isRepUser() && !this.isSiblingOrgUser()) {
            this.quote().salesRepId = this.personLogin.person.personId;
          } else {
            if (sellingOrg.smallTextUserField4 === 'CD') {
              this.quote().salesRepId = sellingOrg.serviceRepId;
            } else {
              this.quote().salesRepId = sellingOrg.responsibilityId;
            }
          }
        }
        this.currentLeadTime.set(this.quote().leadTimeCode);
        this.isBusy.set(false);
      });
    }
  }

  showRepOfficeDropDown(): boolean {
    if (this.isNew()) {
      return this.isSiblingOrgUser() || !this.isRepUser();
    } else {
      return this.userHasRole('IECIT');
    }
  }

  showExpediteParts(): boolean {
    return LeadTimeUtils.isPartsOrder(this.quote().leadTimeCode);
  }

  private selectFirstItemIfMissing(code: string, items: SelectionItem[]): string {
    if (this.isNew()) {
      const filtered = items.filter((item) => item.code == code);
      if (!filtered || filtered.length === 0) {
        if (items && items.length > 0) {
          return items[0].code;
        } else {
          return null!;
        }
      }
    }
    return code;
  }

  public userHasRole(roleName: string): boolean {
    return this.personLoginService.hasRole(RoleName[roleName as keyof typeof RoleName]);
  }

  isPaperPilotUser(): boolean {
    return this.personLoginService.isPaperPilotUser();
  }

  isPaperPilotLeadTime(leadTimeCode: string | undefined): boolean {
    return LeadTimeUtils.isPaperPilotLeadTime(leadTimeCode);
  }

  approvalStatusDisplay(): string {
    if (this.isPaperPilotLeadTime(this.quote()?.leadTimeCode) && this.sentToEwl()) {
      return 'Sent to EWL';
    }
    return this.quote()?.approvalStatus;
  }

  private checkForSentToEwl(): void {
    this.sentToEwl.set(false);
    const key: EntityKey = this.quote()?.id;
    if (!this.isPaperPilotLeadTime(this.quote()?.leadTimeCode) || !key || StringUtils.isBlank(key.entityId)) {
      return;
    }
    this.quoteService.isSentToEwl(key).subscribe({
      next: (sent) => {
        if (this.quote()?.id?.entityId === key.entityId && this.quote()?.id?.entityRevision === key.entityRevision) {
          this.sentToEwl.set(sent);
        }
      },
      error: () => this.sentToEwl.set(false),
    });
  }

  isInternalOrRsm(): boolean {
    return this.personLoginService.isInternalOrRsm();
  }

  public userCanViewOpportunity(): boolean {
    return this.userHasRole('IECCustomerService') || this.userHasRole('IECApplicationEngineer') || this.userHasRole('IECIT') || this.personLoginService.isRsm();
  }

  public formatDisplayDate(input: Date): Date {
    return DateUtils.formatDisplay(input);
  }

  public setExpirationDate(newValue: Date) {
    this.quote().expirationDate = DateUtils.formatOutput(newValue);
  }

  public setSfrPriceDate(newValue: Date) {
    this.quote().sfrPriceBasisDate = DateUtils.formatOutput(newValue);
  }

  public setPriceBasisDate(newValue: Date) {
    this.quote().priceBasisDate = DateUtils.formatOutput(newValue);
  }

  public setRequestedShipDate(newValue: Date) {
    this.quote().requestedShipDate = DateUtils.formatOutput(newValue);
  }

  public copy(): void {
    this.isBusy.set(true);
    this.quoteService.copy(this.quote().id).subscribe({
      next: (result) => {
        this.router.navigate(['quotes', result.updated.id.entityId, result.updated.id.entityRevision]);
      },
      error: (error) => {
        this.handleError(error);
      },
    });
  }

  public multiplier(): void {
    this.resetStatus();
    this.quoteService.runMultiplier(this.quote()).subscribe({
      next: (result) => {
        this.quote.set(result.updated);
        this.saveMessages.set(result.messages);
        this.isBusy.set(false);
        this.loadUpdateUser();
      },
      error: (error) => this.handleError(error),
    });
  }

  public openConfigurator(): void {
    this.router.navigate(['configurator', this.quote().id.entityName, this.quote().id.entityId, this.quote().id.entityRevision]);
  }

  public importQuoteLines(): void {
    this.dialogService
      .showImportQuoteLinesDialog(this.quote(), (response) => {
        this.quote.set(response.updated);
        this.checkForMissingRiserData();
      })
      .subscribe({
        next: (result) => {
          if (result) {
            this.quote.set(result.updated);
            this.checkForMissingRiserData();
            this.showRunMultiplierSnackBar();
          }
        },
        error: (error) => this.handleError(error),
      });
  }

  public onSnBypassChange(checked: boolean): void {
    if (checked) {
      this.dialogService
        .showConfirmationDialog(['Enabling Serial Number Bypass may delay the order as it will require additional review.'], 'Continue', 'Cancel')
        .subscribe((confirmed) => {
          if (!confirmed) {
            this.quote().appEngineerSnBypass = false;
          } else {
            this.save();
          }
        });
    } else {
      this.save();
    }
  }

  public isBypassUser(): boolean {
    return this.userHasRole('IECIT') || this.userHasRole('PartsEngineer');
  }

  public hasLinesRequiringSerialNumber(): boolean {
    if (!this.quote()?.lines) {
      return false;
    }
    return this.quote().lines.some((line) => line.serialNumberRequired && (line.quantity > 1 || !line.serialNumber));
  }

  public getSnBypassDisabledTooltip(): string {
    if (!this.isBypassUser()) {
      return '';
    }
    if (this.isNew()) {
      return 'Save the quote first';
    }
    if (!this.quote().updateAllowed) {
      return 'This quote cannot be edited';
    }
    if (this.quote().appEngineerSnBypass && this.hasLinesRequiringSerialNumber()) {
      return 'Cannot be disabled while there are lines that require a serial number with a missing or invalid quantity';
    }
    return '';
  }

  private showRunMultiplierSnackBar(): void {
    if (!this.quote().frozen && this.quote().updateAllowed) {
      const snackBarRef = this.snackBar.open('Please run the multipler to see updated pricing', 'Run Now', {
        duration: 15000,
      });
      snackBarRef.onAction().subscribe(() => this.multiplier());
      snackBarRef.afterDismissed().subscribe((dismissed) => {
        if (!dismissed.dismissedByAction) {
          setTimeout(() => (this.saveMessages.set(new Array('Please run the multipler to see updated pricing'))), 0);
        }
      });
    }
  }

  public addRisers(): void {
    const riserDialog = this.dialog.open(RiserDialogComponent, {
      disableClose: true,
      width: '1200px',
      maxWidth: '90vw',
      height: '600px',
      data: { entity: this.quote() },
    });
    riserDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.errorMessage.set(undefined!);
        this.quote.set(result);
        this.showRunMultiplierSnackBar();
      }
    });
  }

  public openLeadTimeDialog(): void {
    const leadTimeDialog = this.dialog.open(LeadTimeDialogComponent, {
      disableClose: true,
      data: { leadTimes: this.leadTimes(), entityKey: this.quote().id },
    });
    leadTimeDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.errorMessage.set(undefined!);
        this.showRunMultiplierSnackBar();
        this.quote().lines = result;
      }
    });
  }

  public showLineSummary(): void {
    if (this.selectedLines() && this.selectedLines().length === 1) {
      const selectedLine = this.selectedLines()[0];
      const lineSummaryDialog = this.dialog.open(LineSummaryDialogComponent, {
        disableClose: true,
        data: { line: JSON.parse(JSON.stringify(selectedLine)), appEngineerSnBypass: this.isBypassUser() && this.quote().appEngineerSnBypass },
      });
      lineSummaryDialog.afterClosed().subscribe(() => {
        this.isBusy.set(true);
        this.initialize();
        this.isBusy.set(false);
      });
    }
  }

  public selectQuoteLineRow(clickEvent: any, line: EntityLineHeader): void {
    if (clickEvent.which != 1) {
      return;
    }
    if (!clickEvent.ctrlKey && !clickEvent.shiftKey) {
      this.selectedLines.set([]);
      this.selectedLines.update(current => [...current, line]);
      this.selectionStartIndex = this.quote().lines.indexOf(line);
    } else {
      if (clickEvent.ctrlKey) {
        this.toggleLineSelection(line);
        this.selectionStartIndex = this.quote().lines.indexOf(line);
      }
      if (clickEvent.shiftKey) {
        this.selectedLines.set([]);
        const clickedIndex = this.quote().lines.indexOf(line);
        const first = Math.min(clickedIndex, this.selectionStartIndex);
        const last = Math.max(clickedIndex, this.selectionStartIndex);
        for (let index = first; index <= last; index++) {
          this.selectedLines.update(current => [...current, this.quote().lines[index]]);
        }
      }
    }
  }

  private toggleLineSelection(line: EntityLineHeader): void {
    const index = this.selectedLines().indexOf(line, 0);
    if (index > -1) {
      this.selectedLines.update(current => current.filter((_, lineIndex) => lineIndex !== index));
    } else {
      this.selectedLines.update(current => [...current, line]);
    }
  }

  public isLineSelected(line: EntityLineHeader): boolean {
    return this.selectedLines().indexOf(line, 0) > -1;
  }

  public getSelectedQuoteLineCount(): number {
    return this.selectedLines().length;
  }

  isDiscontinued(): boolean {
    if (!this.selectedLines() || this.selectedLines().length === 0) {
      return false;
    }
    return this.selectedLines().filter((line) => EntityLineUtils.hasDiscontinuedProduct(line)).length > 0;
  }

  public deleteSelectedLines(): void {
    this.resetStatus();
    const remainingLines = this.quote().lines.filter((quoteLine) => !this.isLineSelected(quoteLine));
    this.entityLineService.deleteLines(this.selectedLines()).subscribe(() => {
      this.quote().lines = remainingLines;
      this.selectedLines.set([]);
      this.isBusy.set(false);
      this.showRunMultiplierSnackBar();
    }, this.handleError);
  }

  public addParts(): void {
    this.dialogService.showPartsDialog(this.quote(), this.isBypassUser() && this.quote().appEngineerSnBypass).subscribe({
      next: (result) => {
        if (result) {
          this.quote.set(result);
          this.showRunMultiplierSnackBar();
        }
      },
      error: (error) => this.handleError(error),
    });
  }

  public copyLines(): void {
    const copyLinesDialog = this.dialog.open(CopyLinesDialogComponent, {
      disableClose: true,
      width: '1360px',
      height: '640px',
      data: { lines: JSON.parse(JSON.stringify(this.selectedLines())) },
    });
    copyLinesDialog.afterClosed().subscribe((result) => {
      if (result) {
        if (result.id.entityId == this.quote().id.entityId) {
          this.quote.set(result);
          this.checkForMissingRiserData();
          this.showRunMultiplierSnackBar();
        } else {
          this.isBusy.set(true);
          this.router.navigate(['quotes', result.id.entityId, result.id.entityRevision]);
        }
      }
    });
  }

  public selectAddress(): void {
    this.quoteService.save(this.quote()).subscribe(
      (result) => {
        this.quote.set(result.updated);
        this.saveMessages.set(result.messages);
        this.isBusy.set(false);
      },
      (error) => this.handleError(error),
    );
    this.showAddressDialog();
  }

  private showAddressDialog(): void {
    const addressDialog = this.dialog.open(AddressSelectionDialogComponent, {
      data: {
        entity: JSON.parse(JSON.stringify(this.quote())),
        personLogin: this.personLogin,
        readOnly: this.quote().frozen,
      },
    });
    addressDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.quote.set(result);
      }
    });
  }

  public selectSkyportContact(): void {
    this.entityService.hasSkyportCharacteristic(this.quote().id).subscribe((result) => {
      if (result && result == 'true') {
        this.quoteService.save(this.quote()).subscribe(
          (result) => {
            this.quote.set(result.updated);
            this.saveMessages.set(result.messages);
            this.isBusy.set(false);
          },
          (error) => this.handleError(error),
        );
        this.showSkyportContactDialog();
      }
    });
  }

  private showSkyportContactDialog(): void {
    const skyportDialog = this.dialog.open(SkyportContactDialogComponent, {
      data: { entity: JSON.parse(JSON.stringify(this.quote())) },
      width: '550px',
      height: '380px',
    });
    skyportDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.quote.set(result);
      }
    });
  }

  public fpaManagement(): void {
    this.marketingDataChecks();
    if (!this.errorMessage()) {
      this.resetStatus();
      if (this.hasMissingRiserData()) {
        this.errorMessage.set('Please upload risers via the Riser Chart button and ensure Project Defaults are completed before requesting a FPA.');
        this.isBusy.set(false);
      } else {
        this.quoteService.save(this.quote()).subscribe({
          next: (response: EntitySaveResponse) => {
            this.quote.set(response.updated);
            this.saveMessages.set(response.messages);
            if (LeadTimeUtils.isQuickShip(this.quote().leadTimeCode)) {
              this.errorMessage.set(LeadTimeUtils.QUICK_SHIP_FPA_MESSAGE);
              this.isBusy.set(false);
            } else {
              this.router.navigate(['fpa/QUOTE/' + this.quote().id.entityId + '/' + this.quote().id.entityRevision]);
            }
          },
          error: (error: any) => this.handleError(error),
        });
      }
    }
  }

  private marketingDataChecks(): void {
    this.errorMessage.set(undefined!);
    this.marketSegmentMissing.set(false);
    this.projectTypeMissing.set(false);
    this.specTypeMissing.set(false);
    if (!this.quote().marketSegmentCode || this.quote().marketSegmentCode.length === 0) {
      this.marketSegmentMissing.set(true);
      this.errorMessage.set('Please select a Market Segment\n');
    }
    if (!this.quote().projectTypeCode || this.quote().projectTypeCode.length === 0) {
      this.projectTypeMissing.set(true);
      this.errorMessage.set(this.errorMessage() + 'Please select a Project Type\n');
    }
    if (!this.quote().specTypeCode || this.quote().specTypeCode.length === 0) {
      this.specTypeMissing.set(true);
      this.errorMessage.set(this.errorMessage() + 'Please select a Spec Type');
    }
  }

  private skyportContactDataChecks(): void {
    this.skyportContactMissing.set(false);
    if (this.quote().releaseTypeId == 'SR') {
      this.personService.getSkyportContact(this.quote()).subscribe({
        next: (result) => {
          if (result == null) {
            this.entityService.hasSkyportCharacteristic(this.quote().id).subscribe({
              next: (hasCharacteristic) => {
                if (hasCharacteristic && hasCharacteristic == 'true') {
                  this.skyportContactMissing.set(true);
                  this.errorMessage.set('At least one line on this quote has IEC Skyport Processing selected. You must enter a valid Skyport Contact before placing order.');
                }
              },
              error: (error) => this.handleError(error),
            });
          }
        },
        error: (error) => this.handleError(error),
      });
    }
  }

  private taggingRoomChecks(): void {
    this.taggingRoomMissing.set(false);
    if (this.quote().releaseTypeId == 'SR') {
      this.entityService.isLineWithSkyportMissingRoom(this.quote().id).subscribe({
        next: (result) => {
          if (result && result == 'true') {
            this.taggingRoomMissing.set(true);
            this.errorMessage.set('At least one line on this quote has IEC Skyport Processing selected. You must enter a Room on Tags before placing order.');
          }
        },
        error: (error) => this.handleError(error),
      });
    }
  }

  public viewOrder(id: EntityKey): void {
    this.router.navigate(['orders/' + id.entityId + '/' + id.entityRevision]);
  }

  public viewDocuments(): void {
    this.router.navigate(['documents/' + this.quote().id.entityName + '/' + this.quote().id.entityId + '/' + this.quote().id.entityRevision]);
  }

  public placeOrder(): void {
    if (!this.quote().associatedOrderId && this.quote().leadTimeCode === 'MOK') {
      this.promptForParentEntity();
    } else {
      if (!this.isPaperPilotLeadTime(this.quote().leadTimeCode)) {
        this.marketingDataChecks();
        this.skyportContactDataChecks();
        this.taggingRoomChecks();
      } else {
        this.errorMessage.set(undefined!);
      }
      if (!this.errorMessage()) {
        this.resetStatus();
        this.quoteService.placeOrder(this.quote()).subscribe({
          next: (result) => {
            this.quote.set(result.updated);
            this.isBusy.set(false);
            this.isNew.set(false);
            this.saveMessages.set(result.messages);
            this.loadUpdateUser();
            this.checkForSentToEwl();
          },
          error: (error) => this.handleError(error),
        });
      }
    }
  }

  public viewEntityBreaks(): void {
    const breaksDialog = this.dialog.open(EntityBreakDialogComponent, {
      data: { entityKey: this.quote().id, personLogin: this.personLogin },
    });
    breaksDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.showRunMultiplierSnackBar();
      }
    });
  }

  public cancel(): void {
    const cancelDialog = this.dialog.open(EntityCancelDialogComponent, {
      data: { entityKey: this.quote().id },
    });
    cancelDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.quote.set(result);
        this.loadUpdateUser();
      }
    });
  }

  public showSalesRep(): void {
    if (this.quote().salesRepId && this.quote().salesRepId.length > 0) {
      this.personService.getContactPerson(this.quote().salesRepId).subscribe((result) => {
        const salesRep = result;
        if (salesRep) {
          this.dialog.open(SalesRepDialogComponent, {
            disableClose: true,
            width: '400px',
            height: '150px',
            data: { rep: salesRep },
          });
        }
      });
    }
  }

  public showIecContact(): void {
    if (this.quote().iecContactId && this.quote().iecContactId.length > 0) {
      this.personService.getContactPerson(this.quote().iecContactId).subscribe((result) => {
        const salesRep = result;
        if (salesRep) {
          this.dialog.open(SalesRepDialogComponent, {
            disableClose: true,
            width: '400px',
            height: '150px',
            data: { rep: salesRep },
          });
        }
      });
    }
  }

  public showWarrantyText(): void {
    this.dialog.open(WarrantyTextDialogComponent, {
      disableClose: true,
      width: '400px',
      height: '250px',
      data: { warrantyText: this.quote().warrantyRequestNotes },
    });
  }

  public fixPermissions(): void {
    this.resetStatus();
    this.entityService.fixPermissions(this.quote().id).subscribe({ next: () => (this.isBusy.set(false)), error: (error) => this.handleError(error) });
  }

  public canSubmitSfrs(): boolean {
    const applicableCodes: string[] = ['New', 'SFR Approved'];
    return this.quote().updateAllowed && this.quoteStatusIn(applicableCodes);
  }

  private quoteStatusIn(codes: string[]): boolean {
    return codes.indexOf(this.quote().statusDescription, 0) > -1;
  }

  public canCancelSfrs(): boolean {
    const applicableCodes: string[] = ['SFR Review'];
    return this.quote().updateAllowed && this.quoteStatusIn(applicableCodes);
  }

  public sfrManagement(): void {
    this.router.navigate(['sfrManagement/' + this.quote().id.entityId + '/' + this.quote().id.entityRevision]);
  }

  public submitSfr(): void {
    this.marketingDataChecks();
    if (!this.errorMessage()) {
      this.resetStatus();
      this.sfrService.createRequest(this.quote().id).subscribe({
        next: (result) => {
          this.quote.set(result);
          this.isBusy.set(false);
          this.isNew.set(false);
          this.loadUpdateUser();
        },
        error: (error) => this.handleError(error),
      });
    }
  }

  public cancelSfr(): void {
    this.resetStatus();
    this.sfrService.cancelRequest(this.quote().id).subscribe({
      next: (result) => {
        this.quote.set(result);
        this.isBusy.set(false);
        this.isNew.set(false);
        this.loadUpdateUser();
      },
      error: (error) => this.handleError(error),
    });
  }

  public refreshIecContact(): void {
    this.currentLeadTime.set(this.quote().leadTimeCode);
  }

  canApprove(): boolean {
    switch (this.quote().approvalStatus) {
      case 'Admin Review':
        return this.personLoginService.hasRole(RoleName.IECIT);
      case 'Credit Review':
        return this.personLoginService.hasRole(RoleName.IECIT) || this.personLoginService.hasRole(RoleName.IECCredAnalyst);
      case 'CSR Review':
      case 'Pending Approval':
        return this.personLoginService.hasRole(RoleName.IECIT) || this.personLoginService.hasRole(RoleName.IECCustomerService);
      default:
        return false;
    }
  }

  approveQuote(): void {
    this.resetStatus();
    this.quoteService.approveQuote(this.quote().id).subscribe({
      next: (result) => {
        this.quote.set(result.updated);
        this.isBusy.set(false);
        this.isNew.set(false);
        this.loadUpdateUser();
        this.saveMessages.set(result.messages);
      },
      error: (error) => this.handleError(error),
    });
  }

  openTaggingDialog(): void {
    const taggingDialog = this.dialog.open(TaggingDialogComponent, {
      disableClose: true,
      width: '800px',
      maxWidth: '90vw',
      height: '400px',
      data: { entityKey: this.quote().id },
    });
    taggingDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.isBusy.set(true);
        this.entityService.getEntityByKey(this.quote().id).subscribe({
          next: (result) => {
            this.quote.set(result);
            this.checkForMissingRiserData();
            this.isBusy.set(false);
          },
          error: (error) => this.handleError(error),
        });
      }
    });
  }

  openProjectDefaultsScreen(): void {
    this.router.navigate(['projectDefaults', this.quote().id.entityName, this.quote().id.entityId, this.quote().id.entityRevision]);
  }

  openRiserChartDialog(): void {
    this.router.navigate(['riserChart', this.quote().id.entityName, this.quote().id.entityId, this.quote().id.entityRevision]);
  }

  canAddLines(): boolean {
    return this.quote() && !this.quote().frozen && this.quote().approvalStatus != 'Approved';
  }

  startCreditDocumentWorkflow(): void {
    this.errorMessage.set(undefined!);
    this.isBusy.set(true);
    this.quoteService.startCreditDocumentsWorkflow(this.quote().id).subscribe({
      next: () => {
        this.isBusy.set(false);
        this.checkForCreditDocumentsNeeded();
        this.snackBar.open('Credit documents have been added to the library', 'Close', {
          duration: 10000,
        });
      },
      error: (error) => this.handleError(error),
    });
  }

  getComplexityCategory(): string {
    return this.isComplexityCategoryAssigned() ? this.entityComplexity().complexityCategory.category : 'Unknown';
  }

  showComplexityCategory(): boolean {
    return !this.entityService.isPartsOrWarranty(this.quote());
  }

  private isComplexityCategoryAssigned(): boolean {
    return !!(
      this.entityComplexity() &&
      this.entityComplexity().complexityCategory &&
      this.entityComplexity().complexityCategory.category &&
      this.entityComplexity().complexityCategory.category.trim().length > 0
    );
  }

  getComplexityLeadTime(): string {
    return this.isComplexityCategoryAssigned() ? this.entityComplexity().complexityCategory.leadTimeWeeks + ' weeks' : 'Unknown';
  }

  updateEntityComplexity(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.entityComplexityService.updateEntityComplexity(this.quote().id).subscribe({
      next: (complexity) => {
        this.isBusy.set(false);
        this.entityComplexity.set(complexity);
      },
      error: (error) => this.handleError(error),
    });
  }

  showComplexityInformationDialog(): void {
    this.dialog.open(ComplexityInfoDialogComponent, {
      width: '800px',
      maxWidth: '90vw',
      height: '300px',
      data: { entity: this.quote(), complexity: this.entityComplexity() },
    });
  }

  canUpdateExtendedWarranty(): boolean {
    return ExtendedWarrantyUtils.canWarrantyBeChanged(this.quote());
  }

  showExtendedWarrantyDialog(): void {
    const warrantyDialog = this.dialog.open(ExtendedWarrantyDialogComponent, {
      disableClose: true,
      width: '800px',
      maxWidth: '90vw',
      height: '400px',
      data: { entity: this.quote() },
    });
    warrantyDialog.afterClosed().subscribe({
      next: (result) => {
        if (result) {
          this.isBusy.set(true);
          this.entityService.getEntityByKey(this.quote().id).subscribe({
            next: (entity) => {
              this.quote.set(entity);
              this.isBusy.set(false);
            },
            error: (error) => this.handleError(error),
          });
        }
      },
      error: (error) => this.handleError(error),
    });
  }

  openQewlDialog(): void {
    this.dialog.open(QewlDialogComponent, {
      width: '1360px',
      height: '650px',
      data: this.quote(),
    });
  }

  promptForParentEntity(): void {
    const selectParentEntityDialong = this.dialog.open(SelectParentEntityDialogComponent, {
      width: '1200px',
      height: '700px',
      data: this.quote(),
      disableClose: true,
    });

    selectParentEntityDialong.afterClosed().subscribe({
      next: (parentKey) => {
        if (parentKey !== null && parentKey.length !== 0) {
          this.quote().parentId = parentKey;
          this.marketingDataChecks();
          this.skyportContactDataChecks();
          this.taggingRoomChecks();
          if (!this.errorMessage()) {
            this.resetStatus();
            this.quoteService.placeOrder(this.quote()).subscribe({
              next: (result) => {
                this.quote.set(result.updated);
                this.isBusy.set(false);
                this.isNew.set(false);
                this.saveMessages.set(result.messages);
                this.loadUpdateUser();
              },
              error: (error) => {
                this.handleError(error);
              },
            });
          }
        } else {
          alert('A parent entity must be selected before placing this order.');
        }
      },
      error: (error) => {
        this.handleError(error);
      },
    });
  }

  openCostReport(): void {
    this.router.navigate(['/', 'entityCostReport', this.quote().id.entityId, this.quote().id.entityRevision]);
  }

  openAutoCostReport(): void {
    this.router.navigate(['/', 'autoCostReport', this.quote().id.entityId, this.quote().id.entityRevision]);
  }

  openSureFlowLoopSimulator(): void {
    this.router.navigate(['quotes', 'sureflow', 'loops', this.quote().id.entityId, this.quote().id.entityRevision]);
  }

  isSureFlowUnit(line: EntityLineHeader): boolean {
    const productId = line?.product || '';
    return productId.toUpperCase().indexOf('SUREFLOW') >= 0;
  }

  isSureFlowUnitSelected(): boolean {
    if (!this.selectedLines() || this.selectedLines().length === 0) {
      return false;
    }
    return this.selectedLines().filter((line) => this.isSureFlowUnit(line)).length > 0;
  }

  isAssignNewOpportunityOptionDisplayed(): void {
    if (this.isInternalOrRsm()) {
      this.opportunityService.getAssociatedQuotes(this.quote().opportunityId, this.quote().opportunityRevision).subscribe({
        next: (result) => {
          this.showAssignNewOpportunityOption.set(result.length > 1);
        },
        error: () => {
          this.showAssignNewOpportunityOption.set(false);
        },
      });
    }
  }

  assignNewOpportunity(): void {
    this.resetStatus();
    this.quoteService.assignNewOpportunity(this.quote()).subscribe({
      next: (result) => {
        this.quote.set(result.updated);
        this.saveMessages.set(result.messages);
        const snackBarMessage = 'New Opportunity ID: ' + this.quote().opportunityId + ';' + this.quote().opportunityRevision;
        this.snackBar.open(snackBarMessage, 'Close');
        this.loadUpdateUser();
        this.checkForCreditDocumentsNeeded();
        this.showAssignNewOpportunityOption.set(false);
      },
      error: (error) => {
        this.handleError(error);
      },
      complete: () => {
        this.isBusy.set(false);
      },
    });
  }

  updateOpportunityIfRequired() {
    let daysSinceUpdated = 0;
    this.opportunityService.getOpportunity(this.quote().opportunityId, this.quote().opportunityRevision).subscribe({
      next: (result) => {
        this.associatedOpportunity.set(result);
        daysSinceUpdated = (new Date().getTime() - new Date(this.associatedOpportunity().updateTimestamp).getTime()) / (1000 * 3600 * 24);
        const closed = ['CLS', 'JWON', 'JLST'].includes(result.currentPhaseCode) || ['CLS'].includes(result.statusCode);
        if (!closed && daysSinceUpdated > 90 && this.quote().totalList > 50000) {
          this.openRequiredOpportunityUpdatesDialog();
        }
      },
      error: (error) => this.handleError(error),
    });
  }

  public openRequiredOpportunityUpdatesDialog(): void {
    this.dialog.open(RequireOpportunityUpdatesDialogComponent, {
      disableClose: true,
      width: '500px',
      height: '400px',
      data: { opportunity: this.associatedOpportunity() },
    });
  }

  public enterContact(isPrimaryContact: boolean): void {
    const dialog = this.dialog.open(EnterContactDialogComponent, {
      data: { isPrimaryContact: isPrimaryContact, entity: this.quote() },
    });
    dialog.afterClosed().subscribe((result) => {
      if (result) {
        if (result instanceof HttpErrorResponse) {
          this.handleError(result);
        } else {
          this.quote.set(result);
        }
      }
    });
  }

  private updateFpaStatusFlags(): void {
    this.canCancelFpaReview.set(false);
    this.canSubmitFpa.set(false);
    if (StringUtils.isNotBlank(this.quote()?.id?.entityId)) {
      const fpaStatus: string = this.quote()?.fpaStatus || 'None';
      this.fpaService.isPendingApproval(fpaStatus).subscribe({
        next: (response: string) => {
          this.canCancelFpaReview.set(StringUtils.equalsIgnoreCase('true', response));
          this.canSubmitFpa.set(!this.quote().frozen && !this.canCancelFpaReview());
        },
        error: (error: any) => this.handleError(error),
      });
    }
  }

  showEntityHistoryDialog(): void {
    if (this.quote()) {
      this.dialogService.showEntityHistoryDialog(this.quote());
    }
  }

  showFpaManagementButton(): boolean {
    return this.canSubmitFpa();
  }

  showFpaCancelButton(): boolean {
    return this.canCancelFpaReview();
  }

  cancelFpaReview(): void {
    this.isBusy.set(true);
    this.fpaService.cancelSubmission(EntityKeyUtils.convertToFullEntityKey(this.quote().id)).subscribe({
      next: () => {
        this.entityService.getEntityByKey(this.quote().id).subscribe({
          next: (result: Entity) => {
            this.quote.set(result);
            this.updateFpaStatusFlags();
            this.isBusy.set(false);
          },
          error: (error) => this.handleError(error),
        });
      },
      error: (error: any) => this.handleError(error),
    });
  }

  canCopyLines(): boolean {
    return !this.isBusy() && this.getSelectedQuoteLineCount() > 0 && this.canCopyAllSelectedLines();
  }

  private canCopyAllSelectedLines(): boolean {
    if ((this.selectedLines()?.length || 0) === 0) {
      return false;
    }
    for (const line of this.selectedLines()) {
      if (!line.copyAllowed) {
        return false;
      }
    }
    return true;
  }

  showUnrestrictedEditOption(): boolean {
    return this.quote() && !this.isNew() && this.quote().updateAllowed && this.quote().frozen && this.canUpdateInternalNotes();
  }

  canUpdateInternalNotes(): boolean {
    return (
      this.userHasRole('IECCustomerService') ||
      this.userHasRole('IECIT') ||
      this.userHasRole('IECCredAnalyst') ||
      this.userHasRole('IECApplicationEngineer') ||
      this.userHasRole('IECRSM')
    );
  }

  showUnrestrictedChangesDialog(): void {
    if (this.showUnrestrictedEditOption()) {
      this.dialogService.showUnrestrictedEntityUpdatesDialog(this.quote()).subscribe({
        next: (update: UnrestrictedEntityUpdate) => {
          if (update) {
            this.isBusy.set(true);
            this.errorMessage.set(undefined!);
            this.entityService.saveEntityWithoutRestrictions(update).subscribe({
              next: (updatedEntity: Entity) => {
                this.quote.set(updatedEntity);
                this.isBusy.set(false);
              },
              error: (error: any) => this.handleError(error),
            });
          }
        },
        error: (error: any) => this.handleError(error),
      });
    }
  }

  showRiserChartButton(): boolean {
    return this.isAdminOrCustomerServiceOrBetaTester();
  }

  private isAdminOrCustomerServiceOrBetaTester(): boolean {
    return this.userHasRole('IECIT') || this.userHasRole('IECCustomerService') || this.personLoginService.isBetaTester();
  }

  showProjectDefaultsButton(): boolean {
    return !this.isNew() && this.quote().updateAllowed && this.isAdminOrCustomerServiceOrBetaTester();
  }
}
